import React, { useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  InputAdornment,
  TextField,
  styled,
  useTheme
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useParams, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useFormContext, useWatch } from 'react-hook-form';
import { createCustomerFieldsQueryOptions } from '@/services/ticket/ticket.options';
import { Search, X, ChevronDown, ChevronUp, Link as LinkIcon } from 'lucide-react';

// const StyledAccordion = styled(Accordion)(({ theme }) => ({
//   boxShadow: theme.shadows[3],
//   borderRadius: 4,
//   '&:before': { display: 'none' },
// }));

// const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
//   minHeight: 48,
//   background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
//   color: theme.palette.primary.contrastText,
//   '& .MuiAccordionSummary-content': {
//     margin: theme.spacing(0.5, 0),
//   },
// }));

const TokenChip = styled(Chip)(({ theme }) => ({
  height: 24,
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  // backgroundColor: theme.palette.grey[100],
  backgroundColor: theme.palette.primary.main,
  '& .MuiChip-label': {
    color: theme.palette.primary.contrastText,
    padding: '0 8px',
  },

}));

const TokenCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(0.75),
  minHeight: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  '&:hover': {
    // backgroundColor: theme.palette.grey[50],
    backgroundColor: theme.palette.text.secondary,
  },

}));

const Token = () => {
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const methods = useFormContext()
  const customerWatch = useWatch({ control: methods.control, name: 'customer' })


  const params = useParams({ from: '/_auth/workflow-rules/$workflowruleId' })

  const customFields = useQuery(createCustomerFieldsQueryOptions(customerWatch?.id))

  const universal = [
    { Token: "$(TicketId)", Desc: "Ticket ID" },
    { Token: "$(Status)", Desc: "The status of the ticket" },
    { Token: "$(Customer)", Desc: "The customer of the ticket" },
    { Token: "$(Title)", Desc: "The title of the ticket" },
    { Token: "$(Assignee)", Desc: "The assignee of the ticket, if the ticket is unassigned, will be replaced with 'UNASSIGNED'" },
    { Token: "$(Priority)", Desc: "The priority of the ticket" },
    { Token: "$(Type)", Desc: "The type of the ticket" },
    { Token: "$(ClientName)", Desc: "The client name" },
    { Token: "$(ClientAddress)", Desc: "The client address" },
    { Token: "$(ClientPhone)", Desc: "The client phone number" },
    { Token: "$(ClientEmail)", Desc: "The client email address" }
  ];


  const cfields = customFields?.data?.map((customField: any) => ({
    Token: `$(FieldCid.${customField.Meta.Cid.toString()})`,
    Desc: (
      <Link
        to={`/custom-fields/${customField.Meta.Cid}`}
        search={{
          variant: 'edit',
        }}
        style={{ textDecoration: 'none', color: theme.palette.primary.main }}
      >
        {customField.Meta.Name}
      </Link>
    ),
  })) || [];

  const allFields = [...universal, ...cfields];

  const filteredFields = useMemo(() => {
    return allFields.filter(field =>
      field.Token.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (typeof field.Desc === 'string'
        ? field.Desc.toLowerCase().includes(searchTerm.toLowerCase())
        : field.Desc.props.children.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [allFields, searchTerm]);

  return (
    <Grid size={12} >
      <Box sx={{ mx: 'auto', p: 2 }}>
        <Accordion expanded={isOpen} onChange={() => setIsOpen(!isOpen)} sx={{
          bgcolor: theme.palette.background.paper,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <AccordionSummary
            expandIcon={isOpen ? <ChevronUp color="white" size={16} /> : <ChevronDown color="white" size={16} />}

          >
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Available Tokens</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{
            p: 2,
            maxHeight: '300px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              The following tokens will be replaced by the respective information on the ticket that triggered this workflowrule.
              These tokens work in both the subject and the body. These tokens are case-sensitive.
            </Typography>

            <TextField
              fullWidth
              size="small"
              placeholder="Search tokens..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiInputBase-root': {
                  height: 36,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={14} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ p: 0.5 }}>
                      <X size={14} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{
              width: '100%',
              overflow: 'auto',
              flex: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px',
                '&:hover': {
                  background: '#555',
                },
              },
            }}>
              <Grid container spacing={0.5}>
                {filteredFields.map((item, index) => (
                  <Grid size={12} key={index}>
                    <TokenCard elevation={0} variant="outlined">
                      <TokenChip label={item.Token} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          flex: 1,
                          lineHeight: 1.2,
                          fontSize: '0.75rem',
                        }}
                      >
                        {item.Desc}
                      </Typography>
                    </TokenCard>
                  </Grid>
                ))}
              </Grid>

              {filteredFields.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    No tokens found matching your search.
                  </Typography>
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Grid>



  );
};

export default Token;
